import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  FilePdf, FileCsv, Receipt, Coin, Leaf, TrendUp,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const PERIODS = [
  { key: "daily", label: "Today" },
  { key: "weekly", label: "Last 7 days" },
  { key: "monthly", label: "This month" },
  { key: "yearly", label: "This year" },
  { key: "custom", label: "Custom range" },
];

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const kg = (n) => `${Number(n || 0).toFixed(1)} kg`;

const StatCard = ({ icon: Icon, label, value, tone = "neutral" }) => (
  <div className={`border rounded-md p-5 ${
    tone === "positive" ? "bg-[#D4E0D1]/30 border-[#D4E0D1]"
    : tone === "negative" ? "bg-[#F4C7B9]/30 border-[#F4C7B9]"
    : "bg-white border-[#E5E2D9]"
  }`}>
    <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.22em] text-[#5C5A55]">
      <Icon size={14} weight="duotone" />{label}
    </div>
    <div className={`font-heading text-2xl ${tone === "negative" ? "text-[#7A2E18]" : "text-[#1A1A1A]"}`}>{value}</div>
  </div>
);

export default function WasteReport() {
  const [period, setPeriod] = useState("monthly");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [direction, setDirection] = useState("all"); // all | incoming | outgoing

  const params = useMemo(() => {
    const p = { period };
    if (period === "custom" && fromDate && toDate) {
      p.from_ = new Date(fromDate).toISOString();
      p.to = new Date(toDate + "T23:59:59").toISOString();
    }
    return p;
  }, [period, fromDate, toDate]);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/waste-types/report", { params });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [JSON.stringify(params)]);

  const downloadReport = async (fmt) => {
    try {
      const r = await api.get(`/waste-types/report.${fmt}`, {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], {
        type: fmt === "pdf" ? "application/pdf" : "text/csv",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `waste_report_${period}_${(data?.from || "").slice(0, 10)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (direction === "charge") return data.rows.filter((r) => (r.charge_kg ?? 0) > 0);
    if (direction === "pay") return data.rows.filter((r) => (r.pay_kg ?? 0) > 0);
    return data.rows.filter((r) => (r.billed_kg ?? r.incoming_kg ?? 0) > 0);
  }, [data, direction]);

  const chartData = useMemo(
    () => filteredRows.slice(0, 12).map((r) => ({
      name: r.waste_name.length > 14 ? r.waste_name.slice(0, 13) + "…" : r.waste_name,
      Charges: r.charge_kg ?? 0,
      Payment: r.pay_kg ?? 0,
      Profit: r.profit,
    })),
    [filteredRows],
  );

  const totals = data?.totals || {};

  return (
    <AppShell
      title="Waste Reports"
      subtitle="Service & Pricing · Based on invoices"
      actions={
        <div className="flex items-center gap-2">
          <button data-testid="wr-csv" onClick={() => downloadReport("csv")}
                  className="inline-flex items-center gap-2 h-10 px-4 border border-[#E5E2D9] hover:bg-[#F7F6F2] rounded-md text-sm">
            <FileCsv size={14} /> CSV
          </button>
          <button data-testid="wr-pdf" onClick={() => downloadReport("pdf")}
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm">
            <FilePdf size={14} /> PDF
          </button>
        </div>
      }
    >
      {/* Filters */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((p) => (
            <button key={p.key} data-testid={`wr-period-${p.key}`}
                    onClick={() => setPeriod(p.key)}
                    className={`h-9 px-4 rounded-md text-sm border ${
                      period === p.key ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9] text-[#5C5A55]"
                    }`}>{p.label}</button>
          ))}
          {period === "custom" && (
            <div className="flex items-center gap-2 ml-2">
              <input type="date" data-testid="wr-from" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                     className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm" />
              <span className="text-[#8A8780]">→</span>
              <input type="date" data-testid="wr-to" value={toDate} onChange={(e) => setToDate(e.target.value)}
                     className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm" />
            </div>
          )}
          <div className="ml-auto flex items-center gap-1 text-xs">
            {[["all", "All"], ["charge", "Charges"], ["pay", "Payment"]].map(([d, label]) => (
              <button key={d} data-testid={`wr-dir-${d}`} onClick={() => setDirection(d)}
                      className={`h-8 px-3 rounded-md border ${
                        direction === d ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "border-[#E5E2D9] text-[#5C5A55]"
                      }`}>{label}</button>
            ))}
          </div>
        </div>
        {data?.from && (
          <div className="text-[11px] text-[#8A8780] mt-3">
            {(data.from || "").slice(0, 10)} → {(data.to || "").slice(0, 10)}
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Receipt} label="Billed" value={kg(totals.billed_kg ?? totals.incoming_kg)} />
        <StatCard icon={Receipt} label="Invoices" value={`${totals.invoices ?? 0}`} />
        <StatCard icon={Coin} label="Revenue" value={money(totals.revenue)} />
        <StatCard icon={TrendUp} label="Profit" value={money(totals.profit)}
                  tone={totals.profit >= 0 ? "positive" : "negative"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* Chart */}
        <div className="md:col-span-2 bg-white border border-[#E5E2D9] rounded-md p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-4">
            Charges vs Customer Payment (kg) — top 12
          </div>
          {chartData.length === 0 ? (
            <div className="h-60 flex items-center justify-center text-sm text-[#8A8780]">No invoices in this range.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5C5A55" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5C5A55" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Bar dataKey="Charges" fill="#2D5A27" />
                <Bar dataKey="Payment" fill="#C25D38" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        {/* CO2 */}
        <div className="bg-white border border-[#E5E2D9] rounded-md p-5 flex flex-col justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">Environmental impact</div>
            <div className="flex items-center gap-2 mb-4">
              <Leaf size={20} color="#2D5A27" weight="duotone" />
              <div className="font-heading text-3xl text-[#2D5A27]">{kg(totals.co2_saved_kg)}</div>
            </div>
            <div className="text-xs text-[#5C5A55]">CO₂ emissions avoided based on each waste type's CO₂ factor × billed weight from invoices in this period.</div>
          </div>
          <div className="pt-4 mt-4 border-t border-[#E5E2D9] text-xs text-[#5C5A55]">
            Cost: <span className="font-mono text-[#1A1A1A]">{money(totals.cost)}</span>
          </div>
        </div>
      </div>

      {/* Rows table */}
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="wr-table">
            <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <tr>
                <th className="px-5 py-3 text-left">Waste</th>
                <th className="px-5 py-3 text-left">Type · Txn</th>
                <th className="px-5 py-3 text-right">Billed kg</th>
                <th className="px-5 py-3 text-right">Charge kg</th>
                <th className="px-5 py-3 text-right">Pay kg</th>
                <th className="px-5 py-3 text-right">Invoices</th>
                <th className="px-5 py-3 text-right">Revenue</th>
                <th className="px-5 py-3 text-right">Cost</th>
                <th className="px-5 py-3 text-right">Profit</th>
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={9} className="px-6 py-10 text-center text-sm text-[#8A8780]">Loading…</td></tr>}
              {!busy && filteredRows.length === 0 && (
                <tr><td colSpan={9} className="px-6 py-10 text-center text-sm text-[#8A8780]">No invoices in this range.</td></tr>
              )}
              {filteredRows.map((r, i) => (
                <tr key={r.waste_type_id} className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{r.waste_name}</div>
                    <div className="text-[11px] text-[#8A8780]">{r.category}{r.subcategory ? ` · ${r.subcategory}` : ""}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">{r.type}</span>
                    <span className={`ml-2 px-2 py-0.5 text-[10px] uppercase rounded ${r.transaction_type === "PAY" ? "bg-[#C25D38]/15 text-[#7A2E18]" : "bg-[#2D5A27]/15 text-[#1A3317]"}`}>
                      {r.transaction_type}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono">{(r.billed_kg ?? 0).toFixed(1)}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#2D5A27]">{(r.charge_kg ?? 0).toFixed(1)}</td>
                  <td className="px-5 py-3 text-right font-mono text-[#7A2E18]">{(r.pay_kg ?? 0).toFixed(1)}</td>
                  <td className="px-5 py-3 text-right font-mono">{r.invoice_count ?? 0}</td>
                  <td className="px-5 py-3 text-right font-mono">{money(r.revenue)}</td>
                  <td className="px-5 py-3 text-right font-mono">{money(r.cost)}</td>
                  <td className={`px-5 py-3 text-right font-mono font-semibold ${r.profit >= 0 ? "text-[#2D5A27]" : "text-[#C25D38]"}`}>
                    {money(r.profit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
